import React, { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { EyeInvisibleOutlined, SearchOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { searchesAPI } from '../services/api';
import './ResultsTable.css';

const formatCSVValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /["\n,]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getMonthlySales = (row) => {
  const drops90 = toFiniteNumber(row.keepa_sales_rank_drops_90);
  if (drops90 === null) return null;
  return drops90 / 3;
};

const getCompetitivePrice = (row) => {
  const suggestedLower = toFiniteNumber(row.keepa_suggested_lower_price);
  const competitiveThreshold = toFiniteNumber(row.keepa_competitive_price_threshold);

  if (suggestedLower === null && competitiveThreshold === null) return null;
  if (suggestedLower === null) return competitiveThreshold;
  if (competitiveThreshold === null) return suggestedLower;

  return Math.min(suggestedLower, competitiveThreshold);
};

// Shown instead of 'N/A' when Amazon/Keepa data is missing, so it doesn't read as "not applicable"
const NO_DATA = 'No data';

// Fields that come from Amazon/Keepa
const AMAZON_DATA_FIELDS = new Set([
  'asin',
  'amazon_price',
  'amazon_sales_rank',
  'amazon_url',
  'keepa_sales_rank_drops_90',
  'keepa_referral_fee_percentage',
  'keepa_suggested_lower_price',
  'keepa_competitive_price_threshold',
  'keepa_num_variations',
  'keepa_amazon_30day_lowest_price',
]);
// Fields calculated from the Amazon price
const PROFIT_FIELDS = new Set(['profit', 'minimum_profit', 'exceeds_min_profit']);

const hasAmazonPrice = (row) => row.amazon_price != null && parseFloat(row.amazon_price) > 0;

const getRowClassName = (row) => {
  if (row.filtered_out) return 'filtered-row';

  const reason = String(row.filter_reason || '').trim().toLowerCase();
  if (reason === 'keepa unavailable') return 'legacy-green-result';

  return 'non-filtered-row';
};

export const getResultsTableColumns = (selectedRows, setSelectedRows, onHide) => [
  {
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    size: 40,
    enableSorting: false,
  },
  {
    accessorKey: 'updated_at',
    header: 'Date',
    cell: (info) => {
      const val = info.getValue();
      if (!val) return 'N/A';
      const d = new Date(val);
      const dateStr = `${d.getDate()}/${d.getMonth() + 1}`;
      const isSecondary = info.row.original.secondary_search_id != null;
      
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {dateStr}
          {isSecondary && (
            <span 
              style={{
                display: 'inline-block',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#007bff',
                flexShrink: 0
              }}
              title="Secondary search result"
            />
          )}
        </span>
      );
    },
    csvValue: (row) => {
      const val = row.updated_at;
      if (!val) return '';
      const d = new Date(val);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    },
    size: 55,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    cell: (info) => {
      const value = info.getValue();
      const url = info.row.original.ebay_url;
      return url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="product-link title-cell" title={value}>
          {value}
        </a>
      ) : (
        <span className="title-cell" title={value}>{value}</span>
      );
    },
    csvValue: (row) => row.title || '',
    size: 300,
  },
  {
    accessorKey: 'upc',
    header: 'UPC/EAN',
    cell: (info) => info.getValue() || 'N/A',
    csvValue: (row) => row.upc || '',
    size: 120,
  },
  {
    accessorKey: 'asin',
    header: 'ASIN',
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <a
          href={info.row.original.amazon_url || `https://www.amazon.com/dp/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="product-link"
        >
          {value}
        </a>
      ) : (
        NO_DATA
      );
    },
    csvValue: (row) => row.asin || '',
    size: 100,
  },
  {
    accessorKey: 'ebay_total_price',
    header: 'eBay Price Total',
    cell: (info) => {
      const value = info.getValue();
      if (!value) return 'N/A';
      const { ebay_price, shipping_cost } = info.row.original;
      const price = ebay_price != null ? `$${parseFloat(ebay_price).toFixed(2)}` : 'N/A';
      const shipping = shipping_cost != null && shipping_cost !== ''
        ? (parseFloat(shipping_cost) === 0 ? 'Free' : `$${parseFloat(shipping_cost).toFixed(2)}`)
        : 'N/A';
      const tooltip = `Price: ${price}\nShipping: ${shipping}`;
      return (
        <span title={tooltip} style={{ cursor: 'default', borderBottom: '1px dotted #888' }}>
          ${parseFloat(value).toFixed(2)}
        </span>
      );
    },
    csvValue: (row) => (row.ebay_total_price != null ? `$${parseFloat(row.ebay_total_price).toFixed(2)}` : ''),
    size: 120,
  },
  {
    accessorKey: 'amazon_price',
    header: 'Amazon Price',
    cell: (info) => {
      const value = info.getValue();
      return value ? `$${parseFloat(value).toFixed(2)}` : NO_DATA;
    },
    csvValue: (row) => (row.amazon_price != null ? `$${parseFloat(row.amazon_price).toFixed(2)}` : ''),
    size: 120,
  },
  {
    id: 'competitive_price',
    header: 'Competitive Price',
    cell: (info) => {
      const value = getCompetitivePrice(info.row.original);
      return value != null ? `$${value.toFixed(2)}` : NO_DATA;
    },
    csvValue: (row) => {
      const value = getCompetitivePrice(row);
      return value != null ? `$${value.toFixed(2)}` : '';
    },
    size: 140,
  },
  {
    accessorKey: 'amazon_sales_rank',
    header: 'Amazon SR',
    cell: (info) => {
      const value = info.getValue();
      return value ? value.toLocaleString() : NO_DATA;
    },
    csvValue: (row) => (row.amazon_sales_rank != null ? String(row.amazon_sales_rank) : ''),
    size: 100,
  },
  {
    id: 'monthly_sales',
    header: 'Monthly Sales',
    cell: (info) => {
      const value = getMonthlySales(info.row.original);
      return value != null ? value.toFixed(2) : NO_DATA;
    },
    csvValue: (row) => {
      const value = getMonthlySales(row);
      return value != null ? value.toFixed(2) : '';
    },
    size: 110,
  },
  {
    accessorKey: 'profit',
    header: 'Profit',
    cell: (info) => {
      const value = info.getValue();
      // Profit is calculated as 0 when there's no Amazon price, which looks like a real number
      if (!hasAmazonPrice(info.row.original)) return NO_DATA;
      if (value === null || value === undefined) return 'N/A';
      const profit = parseFloat(value);
      const className = profit > 0 ? 'profit-positive' : 'profit-negative';
      return <span className={className}>${profit.toFixed(2)}</span>;
    },
    csvValue: (row) => (row.profit != null ? `$${parseFloat(row.profit).toFixed(2)}` : ''),
    size: 100,
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    cell: (info) => info.getValue(),
    csvValue: (row) => (row.quantity != null ? String(row.quantity) : ''),
    size: 80,
  },
  {
    accessorKey: 'condition',
    header: 'Condition',
    cell: (info) => info.getValue() || 'N/A',
    csvValue: (row) => row.condition || '',
    size: 70,
  },
  {
    accessorKey: 'ebay_seller',
    header: 'eBay Seller',
    cell: (info) => info.getValue() || 'N/A',
    csvValue: (row) => row.ebay_seller || '',
    size: 120,
  },
  {
    accessorKey: 'seller_feedback',
    header: 'Seller Feedback',
    cell: (info) => {
      const value = info.getValue();
      return value != null ? `${value}%` : 'N/A';
    },
    csvValue: (row) => (row.seller_feedback != null ? `${row.seller_feedback}%` : ''),
    size: 130,
  },
  {
    accessorKey: 'filtered_out',
    header: 'Filtered',
    cell: (info) => {
      const filtered = info.getValue();
      const reason = info.row.original.filter_reason;
      if (filtered) {
        return (
          <span className="badge badge-warning" title={reason || 'Filtered'}>
            {reason || 'Yes'}
          </span>
        );
      }
      return <span className="badge badge-success">No</span>;
    },
    csvValue: (row) => (row.filtered_out ? (row.filter_reason || 'Yes') : 'No'),
    size: 150,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row, column, table }) => {
      const onViewDetails = table.options.meta?.onViewDetails;
      return (
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={() => onViewDetails && onViewDetails(row.original)}
            title="View all saved values"
            style={{ padding: '2px 8px', fontSize: '12px' }}
          >
            <SearchOutlined />
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onHide([row.original.id])}
            title="Hide this result"
            style={{ padding: '2px 8px', fontSize: '12px' }}
          >
            <EyeInvisibleOutlined />
          </button>
        </div>
      );
    },
    size: 130,
    enableSorting: false,
  },
];

export const buildResultsTableCSV = (results, searchTerm) => {
  const columns = getResultsTableColumns().filter(col => col.id !== 'select' && col.id !== 'actions');
  const baseHeaders = [...columns.map((col) => col.header), 'Is Secondary Result', 'eBay URL', 'Amazon URL'];
  const headers = searchTerm != null ? ['Search Term', ...baseHeaders] : baseHeaders;
  const rows = (results || []).map((row) => {
    const rowValues = columns.map((col) => formatCSVValue(
      typeof col.csvValue === 'function' ? col.csvValue(row) : row[col.accessorKey]
    ));
    const isSecondary = formatCSVValue(row.secondary_search_id != null ? 'Yes' : 'No');
    const ebayUrl = formatCSVValue(row.ebay_url);
    const amazonUrl = formatCSVValue(row.amazon_url || (row.asin ? `https://www.amazon.com/dp/${row.asin}` : ''));
    const values = [...rowValues, isSecondary, ebayUrl, amazonUrl];
    return searchTerm != null ? [formatCSVValue(searchTerm), ...values] : values;
  });
  return [headers, ...rows].map((r) => r.join(',')).join('\n');
};

const ResultsTable = ({ results, loading, searchState, statusMessage, onRefresh }) => {
  const [rowSelection, setRowSelection] = useState({});
  const [hiding, setHiding] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  const formatDetailValue = useCallback((key, value, row) => {
    if (PROFIT_FIELDS.has(key) && !hasAmazonPrice(row)) return NO_DATA;
    if (value === null || value === undefined || value === '') {
      return AMAZON_DATA_FIELDS.has(key) ? NO_DATA : 'N/A';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  }, []);

  const detailEntries = useMemo(() => {
    if (!selectedResult) return [];
    return Object.entries(selectedResult);
  }, [selectedResult]);

  const openDetailsModal = useCallback((result) => {
    setSelectedResult(result);
    setDetailsModalOpen(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedResult(null);
  }, []);

  const handleHide = useCallback(async (resultIds) => {
    if (!resultIds || resultIds.length === 0) return;

    try {
      setHiding(true);
      await searchesAPI.hideResults(resultIds);

      // Clear selection after hiding
      setRowSelection({});
      
      // Refresh results if callback provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error hiding results:', error);
      alert(`Failed to hide results: ${error.response?.data?.error || error.message}`);
    } finally {
      setHiding(false);
    }
  }, [onRefresh]);

  const handleHideSelected = useCallback(() => {
    const selectedIds = Object.keys(rowSelection)
      .filter(key => rowSelection[key])
      .map(index => results[parseInt(index)].id);
    
    if (selectedIds.length === 0) {
      alert('Please select at least one result to hide');
      return;
    }

    handleHide(selectedIds);
  }, [rowSelection, results, handleHide]);

  const columns = useMemo(() => getResultsTableColumns(rowSelection, setRowSelection, handleHide), [rowSelection, handleHide]);

  const table = useReactTable({
    data: results,
    columns,
    state: {
      rowSelection,
    },
    meta: {
      onViewDetails: openDetailsModal,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const downloadCSV = () => {
    const csv = buildResultsTableCSV(results);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `search_results_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!searchState || searchState === 'new') {
    return (
      <div className="results-container">
        <div className="no-results">
          Run a search to see results
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="results-container">
        <div className="loading">Loading results...</div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="results-container">
        <div className="no-results">
          {searchState === 'in_progress' ? 'Search is in progress...' : 'No results found'}
        </div>
      </div>
    );
  }

  return (
    <div className="results-container">
      <div className="results-header">
        <h3>
          Results ({results.length})
          {searchState === 'in_progress' && (
            <span className="badge badge-running" style={{ fontSize: '11px', marginLeft: '8px', verticalAlign: 'middle' }}>In Progress</span>
          )}
        </h3>
        {statusMessage && searchState === 'in_progress' && (
          <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px', marginBottom: '8px' }}>
            {statusMessage}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="btn btn-outline-danger btn-sm" 
            onClick={handleHideSelected}
            disabled={hiding || Object.keys(rowSelection).length === 0}
            title="Hide selected results"
          >
            <EyeInvisibleOutlined /> {hiding ? 'Hiding...' : 'Hide Selected'}
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={downloadCSV}>
            ⬇ Download CSV
          </button>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="table results-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.column.columnDef.size }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="header-cell">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() && (
                        <span className="sort-indicator">
                          {header.column.getIsSorted() === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={getRowClassName(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        title="Result Details"
        open={detailsModalOpen}
        onCancel={closeDetailsModal}
        footer={null}
        width={700}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <table className="table table-sm" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {detailEntries.map(([key, value]) => (
                <tr key={key}>
                  <td><strong>{key}</strong></td>
                  <td style={{ wordBreak: 'break-word' }}>{formatDetailValue(key, value, selectedResult)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};

export default ResultsTable;
